import { takesMoney } from "../utils/takesMoney";
import { openConnectBank, openMoneyGate, whichGate } from "../lib/connectBank";
import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { ordersApi } from "../services/orders";
import { usersApi } from "../services/users";
import "./ProfilePage.css";
import { User } from "../types/User";
import { FaChevronRight } from "react-icons/fa";
import {
  subscribeAcceptingStatus,
  seedAcceptingStatusFromFetch,
} from "../lib/acceptingStatus";
import {
  subscribeLiveControlVisible,
  seedLiveControlVisibleFromFetch,
} from "../lib/liveControlVisibility";
import RequestPage from "./RequestPage";
import MorePage from "./MorePage";
import EarningsInsights from "../components/EarningsInsights";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useProfileDrawer } from "../lib/profileDrawer";
import type { IgVerifyStatus } from "../lib/instagramLogin";
import { roundPriceStep } from "../utils/roundPriceStep";
import { pageCache } from "../lib/pageCache";
import ProfileDrawerSkeleton from "../components/ProfileDrawerSkeleton";
import { PayinCurrency } from "../utils/payinConfig";
import FeeTierSheet, { FeeTierStatus } from "../components/FeeTierSheet";
import { SHOW_TIERS } from "../lib/tiersPaused";
import ReferEarnSheet from "../components/ReferEarnSheet";
import { PROFILE_ICONS } from "../components/profileMenuIcons";
import type { ReferralStats } from "../services/users";

import { Capacitor } from "@capacitor/core";
import { absMediaUrl } from "../utils/mediaUrl";
import { useAvatarCropper } from "../hooks/useAvatarCropper";
import AvatarCropModal from "../components/AvatarCropModal";
import { useProfileAutosave } from "../hooks/useProfileAutosave";
import { useProfileSettings } from "../hooks/useProfileSettings";
import { useStorefront } from "../hooks/useStorefront";
import { usePendingBalance, WelcomeBanner, PendingPayoutWidget } from "../components/PendingPayout";
import ProfileHeadRow from "../components/ProfileHeadRow";
import { talentTickEarned } from "../utils/talentTick";
import PreviewTip from "../components/PreviewTip";
import { endHint, hintDue } from "../lib/welcomeHints";
import RequestPriceCard from "../components/RequestPriceCard";
import EditProfileSheet from "../components/EditProfileSheet";
import ProfileIgConnect from "../components/ProfileIgConnect";
import type { TalentIgEntry } from "../components/TalentIgConnect";
import LeaderboardViews from "../components/LeaderboardViews";
import DiscoverSheet from "../components/DiscoverSheet";
import BottomSheet from "../components/BottomSheet";
import ProfileSettingsSheet from "../components/ProfileSettingsSheet";
import StorefrontSheets from "../components/StorefrontSheets";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

const MAX_LINKS = 5;
// Phase C: all live pay-in currencies (mirror of server
// SUPPORTED_PAYIN_CURRENCIES via utils/payinConfig.ts).
type Currency = PayinCurrency;

const isNonEmpty = (s: string) => s.trim().length > 0;

const normalizeUrl = (v: string) => {
  const s = v.trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[a-z]+:\/\//i.test(s)) return s;
  return `https://${s}`;
};

// The Leaderboard row's thin line trophy (founder, 2026-09-15) — on the row and
// on its sheet's title.
const TROPHY_ICON = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 4h8v5a4 4 0 0 1-8 0V4z"/><path d="M8 5.5H5.5a2 2 0 0 0 0 4H8"/><path d="M16 5.5h2.5a2 2 0 0 1 0 4H16"/><path d="M12 13v4"/><path d="M9 21h6l-1-4h-4z"/></svg>
);

const ProfilePage: React.FC = () => {
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  // The tap on the Profile TAB must never also press whatever the panel draws
  // under that finger (iOS delivered it to "Follow us on Instagram", which sits
  // right over the tab — found on the simulator, build 38). The panel swallows
  // clicks until its slide-in animation has finished (profile-panel-in, 0.28s;
  // 0.01ms under reduced motion). Fallback: 400ms, for when no animation runs.
  const panelSettled = useRef(false);
  useEffect(() => {
    const t = window.setTimeout(() => (panelSettled.current = true), 400);
    return () => window.clearTimeout(t);
  }, []);
  const swallowOpeningTap = (e: React.MouseEvent) => {
    if (!panelSettled.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  // Seed from the session cache so tab revisits render instantly instead of
  // flashing "Loading..." (same recipe as QR/Balance, founder call).
  // The fetch below still runs every mount and overwrites silently.
  const [profile, setProfile] = useState<User | null>(
    (user?.slug && pageCache.get<User>(`profile:${user.slug}`)) || null
  );
  // Free tier: the review sheet (components/ReviewSheet) saves an Instagram
  // handle from anywhere; the Request Price card's line follows at once.
  useEffect(() => {
    const onPatched = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (d.approvalStatus) setProfile((p) => (p ? { ...p, approvalStatus: d.approvalStatus, instagram: d.instagram ?? p.instagram, links: d.links ?? p.links } : p));
      // The bank sheet: a MassPay ID check moved on, or the country was changed.
      if (d.payout !== undefined) setProfile((p) => (p ? { ...p, payout: d.payout, bankCountry: d.bankCountry ?? p.bankCountry } : p));
    };
    window.addEventListener("profile:patched", onPatched);
    return () => window.removeEventListener("profile:patched", onPatched);
  }, []);
  // Which bottom-sheet drawer is open (Edit profile / Settings / Boring
  // stuff). null = none. Leaderboard opens as a sheet too since 1.5.2 b48
  // (the /leaderboard page stays for links). "settings" = the merged Notifications + Preferences sheet
  // (founder: two rows condensed to one).
  const [openSheet, setOpenSheet] = useState<null | "edit" | "settings" | "merch" | "setlists" | "feeTier" | "refer" | "leaderboard" | "discover">(null);
  // Back from "Verify with Instagram" (pages/InstagramCallbackPage lands on
  // /requests?igVerify=…#profile): open the Edit-profile sheet straight away,
  // so the result is on screen instead of behind a tap. Then the param leaves
  // the URL (2026-10-10): closing the drawer keeps the query, so every later
  // Profile open reopened Edit profile with the same Instagram note. The
  // result itself stays here, for that note.
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const igVerifyParam = (searchParams.get("igVerify") || null) as IgVerifyStatus | null;
  const [igVerify, setIgVerify] = useState<IgVerifyStatus | null>(igVerifyParam);
  // Connect Instagram over the drawer (components/ProfileIgConnect): the
  // message after Instagram's sign-in said "personal account"; with both
  // switches on, also Verify / the Instagram box (the screen first, founder
  // build 52), and "Instagram connected!" after the sign-in vouched.
  // Switches off: the drawer, as before.
  const [igConnect, setIgConnect] = useState<{ entry: TalentIgEntry; n: number } | null>(null);
  const openIgConnect = (entry: TalentIgEntry) => setIgConnect((c) => ({ entry, n: (c?.n ?? 0) + 1 }));
  useEffect(() => {
    if (!igVerifyParam) return;
    setIgVerify(igVerifyParam);
    if (igVerifyParam === "personal" || igVerifyParam === "done") openIgConnect(igVerifyParam);
    else setIgConnect(null);
    setOpenSheet("edit");
    const rest = new URLSearchParams(location.search);
    rest.delete("igVerify");
    const search = rest.toString();
    // navigate, not setSearchParams: that one drops the #profile hash, and the drawer with it.
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
      { replace: true, state: location.state }
    );
    // Once per arrival; the rest of the URL is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [igVerifyParam]);
  // Back from a talent opened in Discover (1.5.3): the fan page's back arrow
  // (or the phone's back) returns to this history entry, which carries
  // reopenSheet — so Discover is open again, as the talent left it. The flag
  // is taken off at once: a later visit to this entry opens the drawer only.
  useEffect(() => {
    const st = location.state as { reopenSheet?: string } | null;
    if (st?.reopenSheet !== "discover") return;
    setOpenSheet("discover");
    const rest: Record<string, unknown> = { ...st };
    delete rest.reopenSheet;
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: rest });
    // Once per mount: the drawer mounts each time it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bumped by the Lifetime Earnings row inside the Edit-profile drawer; the Insights
  // sheet (EarningsInsights, mounted in the main list) opens on each bump.
  const [insightsSignal, setInsightsSignal] = useState(0);
  // Same idea for Settings' "Help, terms & support" row: it opens the chooser the More menu owns.
  const [helpSignal, setHelpSignal] = useState(0);
  // Earnings figures ride the profile fetch. Shared by the Insights sheet and
  // the Lifetime Earnings row inside the Edit-profile drawer (founder 2026-09-15).
  const earningsByCurrency = (profile as any)?.earnedByCurrency || {};
  const earningsAmount = Number((profile as any)?.earnedAmount) || 0;
  const earningsCurrency = (profile?.currency || "usd").toLowerCase();

  // Loyalty fee tiers (ships dark behind the server's FEE_TIERS flag): the
  // Platform Fee row + sheet exist only when GET /orders/fee-tier says
  // enabled — while the flag is off the payload is {enabled:false} and this
  // stays null, so the menu renders byte-identically to today. Cached per
  // slug so a reopened drawer doesn't reflow the menu while the fetch runs.
  // Refer & Earn: same shape as the tier row. An older server or a failed
  // fetch = stays null and the row never renders. `eligible` is the server's
  // word (approved AND banked where we can pay); an UNAPPROVED talent still
  // sees the row (founder 9/23) and taps into the same verification card the
  // QR tab shows — see showRefer / referLocked below.
  const [referrals, setReferrals] = useState<ReferralStats | null>(
    (user?.slug && pageCache.get<ReferralStats>(`referrals:${user.slug}`)) || null
  );
  useEffect(() => {
    if (!user?.slug) return;
    let cancelled = false;
    usersApi
      .referrals()
      .then((res) => {
        if (cancelled) return;
        if (res?.eligible) pageCache.set(`referrals:${user.slug}`, res);
        setReferrals(res || null);
      })
      .catch(() => {
        /* non-fatal — the row simply doesn't render */
      });
    return () => {
      cancelled = true;
    };
  }, [user?.slug]);

  // Refer & Earn is money, so it follows the money rule (founder, 2026-09-29):
  // while the talent can't take money yet the row is there and a tap opens the
  // same two sheets as every other money button (lib/connectBank) — never a
  // locked teaser. Approved but not eligible (not offered in their bank
  // country) = no row, as before.
  const referLocked = !takesMoney(profile);
  const showRefer = !!referrals && (referrals.eligible || referLocked);
  // No bank yet (founder, build 44: "don't let people see Lifetime Earnings or
  // Tier until after they connect bank"): Stats opens Connect Bank.
  const moneyLocked = !takesMoney(profile) && whichGate(profile) === "bank";

  // PAUSED (founder 2026-10-22, lib/tiersPaused): no fetch, no tier anywhere.
  const [feeTier, setFeeTier] = useState<FeeTierStatus | null>(
    (SHOW_TIERS && user?.slug && pageCache.get<FeeTierStatus>(`feeTier:${user.slug}`)) || null
  );
  useEffect(() => {
    if (!user?.slug || !SHOW_TIERS) return;
    let cancelled = false;
    ordersApi
      .feeTier()
      .then((res) => {
        if (cancelled) return;
        if (res?.enabled) {
          pageCache.set(`feeTier:${user.slug}`, res);
          setFeeTier(res);
        } else {
          setFeeTier(null);
        }
      })
      .catch(() => {
        /* non-fatal — the row simply doesn't render */
      });
    return () => {
      cancelled = true;
    };
  }, [user?.slug]);
  // Every field below starts from the SAME cached profile the panel itself
  // renders from (falling back to the signed-in user for the name). They used
  // to start empty and fill in when the fetch landed — the name, Instagram
  // chip, bio bubble and price popped in a beat after the panel opened and
  // nudged the whole layout (founder, build 39). The fetch still overwrites.
  const seed = profile as (User & { venueName?: string; links?: string[] }) | null;
  const [name, setName] = useState(seed?.name || user?.name || "");
  const [currency, setCurrency] = useState<Currency>(((seed?.currency || user?.currency || "usd") as string).toLowerCase() as Currency);
  // Drawer exit (the panel is the #profile push-drawer over the
  // current tab). X / scrim-tap close via the shared hook, which pops the
  // history entry the drawer pushed — or strips the hash in place when the
  // entry below is unknown (post-login landing), so you never bounce to
  // /login. The tab underneath never changed, so "exit" = just close.
  const { close: exitProfilePanel } = useProfileDrawer();

  const [instagram, setInstagram] = useState<string>((seed?.instagram || "").replace(/^@+/, ""));
  const [bio, setBio] = useState<string>(seed?.bio || "");
  const [venueName, setVenueName] = useState<string>(seed?.venueName || "");
  const [links, setLinks] = useState<string[]>(Array.isArray(seed?.links) ? seed!.links! : []);
  const [requestPriceInput, setRequestPriceInput] = useState(seed?.pricePerRequest != null ? String(seed.pricePerRequest) : "");
  const [profileMode, setProfileMode] = useState<"edit" | "preview">("edit");
  // "Preview my public page" hops until the talent's first preview (founder,
  // build 44) — from the green link or Edit profile, either counts. This page
  // mounts each time the drawer opens, so reading it here is always fresh.
  const [previewHint, setPreviewHint] = useState(() => hintDue("preview", user?.slug));
  const openPreview = () => {
    if (user?.slug) endHint("preview", user.slug);
    setPreviewHint(false);
    setProfileMode("preview");
  };
  // A talent tapped in Discover. Someone else: their fan page, where this
  // talent can request and tip like any fan (the payment is a guest's, or
  // their fan account's when one is signed in on this phone — never this
  // talent account's); this entry is marked so coming back reopens Discover.
  // Their own card: their Preview — never a checkout to themselves.
  const openFromDiscover = (slug: string) => {
    setOpenSheet(null);
    if (slug === user?.slug) {
      openPreview();
      return;
    }
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: { ...((location.state as object | null) || {}), reopenSheet: "discover" } }
    );
    navigate(`/${slug}`, { state: { fromBrowse: true } });
  };
  const [isAccepting, setIsAccepting] = useState(true);
  const isEdit = profileMode === "edit";
  const isPreview = profileMode === "preview";

  const autosave = useProfileAutosave({ user, setProfile });
  const { fieldStatus, setSaving, markSaved, setSaveError, putProfileField } = autosave;
  const avatar = useAvatarCropper({ user, setProfile, initialAvatarUrl: seed?.avatarUrl || user?.avatarUrl || null });
  const { avatarPreview, setAvatarPreview, avatarSrc, cropSrc } = avatar;
  const settings = useProfileSettings({ autosave, isAccepting, setIsAccepting });
  const {
    setIsWebPushEnabled,
    showFastPass,
    setShowFastPass,
    allowPersonalizedMessages,
    setAllowPersonalizedMessages,
    setShowLiveToggle,
    setEmailPayoutStarted,
    setEmailDepositLanded,
    setRequestCardSize,
  } = settings;
  const usesStorefront =
    !!profile?.setlistSaleEnabled || profile?.setlistPrice != null || (profile?.shopPlaylists?.length ?? 0) > 0;
  const nativeApp = Capacitor.isNativePlatform();
  // Selling is money, so Sell Playlists follows the money rule (Refer & Earn
  // above): the row is there, and until the talent can take money a tap opens
  // the money sheets, because fans can't see their playlists yet. Talent who
  // already use it keep every control.
  const storefrontLocked = !takesMoney(profile) && !usesStorefront;
  const storefront = useStorefront({ user, profile, currency, setOpenSheet, autosave });
  const { setSetlistPriceInput, setSetlistEnabled, setStorefrontV2 } = storefront;
  // The row's On/Off (founder, build 55): On = a fan can buy a playlist from
  // them right now, one on the shelf or tonight's set, and they can take money.
  const sellingPlaylists = takesMoney(profile) && (storefront.playlistsOnSale > 0 || storefront.setlistEnabled);
  const { pendingBalance, showWelcome, dismissWelcome, totalPendingDollars, totalPendingCount, platformPendingDollars } = usePendingBalance();

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    usersApi.get(user.slug).then((res) => {
      const data = res as User & {
        links?: string[];
        instagram?: string;
        avatarUrl?: string;
        earnedByCurrency?: Record<string, { gross: number; net: number; count: number }>;
        earnedAmount?: number;
      };

      if (user?.slug) pageCache.set(`profile:${user.slug}`, data);
      setProfile(data);
      setName(data.name);
      setAvatarPreview(absMediaUrl(data.avatarUrl));
      setCurrency((data.currency || "usd") as Currency);
      const initialPriceStr =
        data.pricePerRequest != null && data.pricePerRequest !== undefined
          ? String(data.pricePerRequest)
          : "";
      setRequestPriceInput(initialPriceStr);
      setInstagram((data.instagram || "").replace(/^@+/, ""));
      setBio(data.bio || "");
      setVenueName((data as any).venueName || "");
      // Only seeds the cache if no toggle has happened yet — never overrides
      // a fresh toggle with a possibly-stale fetch. Subscriber syncs state.
      seedAcceptingStatusFromFetch((data as any).isAcceptingRequests !== false);
      setShowLiveToggle((data as any).showLiveToggle === true);
      seedLiveControlVisibleFromFetch((data as any).showLiveToggle === true);

      setIsWebPushEnabled(data.isWebPushEnabled !== false);
      setShowFastPass((data as any).showFastPass !== false);
      setAllowPersonalizedMessages((data as any).allowPersonalizedMessages !== false);
      setEmailPayoutStarted((data as any).emailPayoutStarted !== false);
      setEmailDepositLanded((data as any).emailDepositLanded !== false);
      // Show the DJ the price they actually saved (founder decision). The
      // old floor-clamp on display was half of the "can't enter anything
      // below $4.99" bug: a saved $3 rendered as $4.99, so the DJ couldn't
      // even see that their number had been overridden. 0 is a real saved
      // price (free) and must survive — hence >= 0 and an explicit
      // null/undefined check for "never set one".
      const rawSetlist = (data as any).setlistPrice;
      const savedSetlist = Number(rawSetlist);
      setSetlistPriceInput(
        rawSetlist !== null && rawSetlist !== undefined && Number.isFinite(savedSetlist) && savedSetlist >= 0
          ? String(savedSetlist)
          : ""
      );
      setSetlistEnabled((data as any).setlistSaleEnabled === true);
      // === true, not truthy: an older server sends nothing here and the
      // whole v2 surface must stay invisible in that case.
      setStorefrontV2((data as any).storefrontV2 === true);
      setRequestCardSize(((data as any).requestCardSize as any) || "compact");

      const arr = Array.isArray(data.links) ? data.links.slice(0, MAX_LINKS) : [];
      setLinks(arr);

    });
    // The setters above come from the split hooks (stable setState dispatchers);
    // the fetch is keyed on sign-in only, exactly as before the split.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAuthenticated]);

  // Stay in sync when status is toggled from another place (e.g. Header pill)
  useEffect(() => subscribeAcceptingStatus(setIsAccepting), []);
  useEffect(() => subscribeLiveControlVisible(setShowLiveToggle), [setShowLiveToggle]);

  // Hide the floating Live pill while the drawer is up: it's not part of
  // the pushed shell (it would hover un-pushed over the dimmed strip).
  // The panel itself now sits at z:902 — see the STACKING CONTRACT note
  // in ProfilePage.css before touching any z here.
  useEffect(() => {
    document.body.classList.add("profile-panel-open");
    return () => document.body.classList.remove("profile-panel-open");
  }, []);

  // ===== Links handlers =====
  // (handleAddLink parked with the "+ Add link" button — push an
  //  empty string onto `links`, capped at MAX_LINKS, when it returns.)
  const handleChangeLink = (index: number, value: string) => {
    setLinks((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleRemoveLink = (index: number) => {
    const next = links.filter((_, i) => i !== index);
    setLinks(next);
    saveLinks(next);
  };

  // Currency: save the instant the dropdown changes. On failure, snap the
  // dropdown back to the saved value and show a clear error — never a silent
  // revert (that was the original bug).
  const handleCurrencyChange = async (next: Currency) => {
    if (!user || next === currency) return;
    const prev = currency;
    setCurrency(next);
    setSaving("currency");
    try {
      const data = await usersApi.setCurrency(user.slug, next);
      // If the old price number didn't fit the new currency, the server reset
      // it to the currency's placeholder (and re-armed the setup popup) —
      // reflect that in the price box immediately.
      const serverPrice = typeof data?.pricePerRequest === "number" ? data.pricePerRequest : null;
      setProfile((p) => (p ? { ...p, currency: next, ...(serverPrice != null ? { pricePerRequest: serverPrice } : {}) } : p));
      if (serverPrice != null) setRequestPriceInput(String(serverPrice));
      markSaved("currency");
    } catch (e) {
      console.error("Currency save failed:", e);
      setCurrency(prev);
      setSaveError(
        "currency",
        tr("profilePage.couldnTSaveYour")
      );
    }
  };

  // Links: save the whole list on blur of any row and after a row is removed.
  const saveLinks = async (linksArr: string[]) => {
    const cleaned = linksArr
      .map(normalizeUrl)
      .filter(isNonEmpty)
      .slice(0, MAX_LINKS);
    if (JSON.stringify(cleaned) === JSON.stringify(profile?.links || [])) return;
    const ok = await putProfileField("links", { links: JSON.stringify(cleaned) });
    if (ok) setProfile((p) => (p ? { ...p, links: cleaned } : p));
  };

  if (!profile)
    return (
      <>
        <div className="profile-scrim" onClick={exitProfilePanel} aria-hidden="true" />
        <div className="profile-container">
          <ProfileDrawerSkeleton />
        </div>
      </>
    );

  const previewRequestPriceValue = (): number => {
    const t = requestPriceInput.trim();
    if (t === "") return roundPriceStep(Number(profile.pricePerRequest) || 0, currency);
    const n = Number(t.replace(",", "."));
    return Number.isFinite(n)
      ? roundPriceStep(n, currency)
      : roundPriceStep(Number(profile.pricePerRequest) || 0, currency);
  };

  const previewAvatarUrl = absMediaUrl(avatarPreview) ?? absMediaUrl(profile.avatarUrl);

  return (
    <>
    {/* Dimmed strip left of the side panel — tap to slip back out. */}
    <div className="profile-scrim" onClick={exitProfilePanel} aria-hidden="true" />
    <div
      className={`profile-container${isEdit ? " profile-container--edit" : " profile-container--preview"}`}
      onClickCapture={swallowOpeningTap}
      onAnimationEnd={(e) => {
        if (e.animationName === "profile-panel-in") panelSettled.current = true;
      }}
    >
      {showWelcome && <WelcomeBanner onDismiss={dismissWelcome} />}
      {/* The Live/Offline pill floats in the top-right corner of the app pages
          (see LiveStatusFloat) — opt-in via Preferences, and force-shown while
          Offline. It's no longer in the header and not a row here. */}

      {/* Preview mode's way back — the Edit/Preview toggle became the
          "Preview my public page" button below (2026-08 Fanvue restyle). */}
      {isPreview && (
        <>
          <button
            type="button"
            className="profile-preview-back"
            onClick={() => setProfileMode("edit")}
          >
            <T k="profilePage.backToMyProfile" tags={{ 0: (c) => <span className="profile-preview-back-chev" aria-hidden="true">{c}</span> }} />
          </button>
          {/* The banner lives OUTSIDE the embedded page (founder decision) so the
              preview below is exactly what fans see — nothing extra inside. */}
          <div className="rq-profile-preview-banner" role="status">
            {tr("profilePage.previewFansSeeThis")}
            {/* Neither a venue nor a bio yet: a second line that takes them there. */}
            {!venueName.trim() && !bio.trim() && (
              <PreviewTip
                onTap={() => {
                  setProfileMode("edit");
                  setOpenSheet("edit");
                }}
              />
            )}
          </div>
        </>
      )}

      {isEdit && (
      <>
      <ProfileHeadRow
        bio={bio}
        avatarSrc={avatarSrc}
        defaultAvatarPath={avatar.defaultAvatarPath}
        isAccepting={isAccepting}
        user={user}
        name={name}
        instagram={instagram}
        verified={!!instagram.trim() && talentTickEarned(profile)}
        onEdit={() => setOpenSheet("edit")}
      />

      <RequestPriceCard
        user={user}
        profile={profile}
        currency={currency}
        requestPriceInput={requestPriceInput}
        setRequestPriceInput={setRequestPriceInput}
        setProfile={setProfile}
        handleCurrencyChange={handleCurrencyChange}
        autosave={autosave}
      />

      <div className="profile-divider" aria-hidden="true" />
      </>
      )}

      {isEdit ? (
      <div className="profile-form">
        {/* ── Menu list — the 3 core editing drawers. Icons are brand-green
            (Option H). Leaderboard / Support / Terms / Privacy / account actions
            moved to the new "More" tab. ── */}
        <div className="profile-menu">
          {/* Leaderboard — promoted from the More group to the top row
              (founder, 2026-08-05). Was a gold trophy EMOJI (founder,
              2026-08-07); now a thin brand-green line trophy like every other
              row's icon (founder, 2026-09-15, build 37). */}
          <button type="button" className="profile-menu-row" onClick={() => setOpenSheet("leaderboard")}>
            <span className="profile-menu-ic" aria-hidden="true">
              {TROPHY_ICON}
            </span>
            <span className="profile-menu-label">{tr("profilePage.leaderboard")}</span>
            <FaChevronRight className="profile-menu-chev" aria-hidden />
          </button>
          {/* Discover (1.5.3, founder 10/26) — the fans' Browse screen for
              talent: find other artists, open their page, request and tip. */}
          <button type="button" className="profile-menu-row" onClick={() => setOpenSheet("discover")}>
            <span className="profile-menu-ic" aria-hidden="true">
              {PROFILE_ICONS.discover}
            </span>
            <span className="profile-menu-label">{tr("profilePage.discover")}</span>
            <FaChevronRight className="profile-menu-chev" aria-hidden />
          </button>
          {/* Stats (founder, 1.5.2) — took the "My Profile" row's
              place (the account card above opens the profile now) and the
              Lifetime Earnings card's job in the drawer: it opens the stats
              sheet below. No bank yet → Connect Bank, like every money row. */}
          <button
            type="button"
            className="profile-menu-row"
            onClick={() => (moneyLocked ? openMoneyGate(profile) : setInsightsSignal((n) => n + 1))}
          >
            <span className="profile-menu-ic" aria-hidden="true">
              {PROFILE_ICONS.stats}
            </span>
            <span className="profile-menu-label">{tr("profilePage.requestStats")}</span>
            <FaChevronRight className="profile-menu-chev" aria-hidden />
          </button>
          {/* The stats sheet only (showRow={false}); the row is above. */}
          <EarningsInsights
            showRow={false}
            openSignal={insightsSignal}
            earnedByCurrency={earningsByCurrency}
            earnedAmount={earningsAmount}
            ownCurrency={earningsCurrency}
            tierNext={
              // "$X to go → Silver" under Lifetime earnings (founder decision) —
              // same numbers the Tier Level sheet chases; hidden on custom
              // rates and at Legend (nothing left to chase).
              feeTier?.enabled && feeTier.next && !feeTier.overrideActive
                ? { remaining: feeTier.next.remaining, name: feeTier.next.name }
                : null
            }
          />
          {/* Refer & Earn, right under the money rows. The pill is the COUNT,
              never money (founder 9/19), and only once somebody joined. */}
          {showRefer && (
            <button type="button" className="profile-menu-row" onClick={() => (referLocked ? openMoneyGate(profile) : setOpenSheet("refer"))}>
              <span className="profile-menu-ic" aria-hidden="true">
                {PROFILE_ICONS.refer}
              </span>
              <span className="profile-menu-label">{tr("profilePage.referEarn")}</span>
              {referrals.joined > 0 && <span className="profile-menu-val">{tr("profilePage.joined", { joined: referrals.joined })}</span>}
              <FaChevronRight className="profile-menu-chev" aria-hidden />
            </button>
          )}
          {/* Settings = Notifications + Preferences in ONE row + sheet
              (founder 8/29). No label on the row (founder, build 55). */}
          <button type="button" className="profile-menu-row" onClick={() => setOpenSheet("settings")}>
            <span className="profile-menu-ic" aria-hidden="true">
              {PROFILE_ICONS.settings}
            </span>
            <span className="profile-menu-label">{tr("profilePage.settings")}</span>
            <FaChevronRight className="profile-menu-chev" aria-hidden />
          </button>
          {/* Request History left this menu — it now opens from the
              Requests page's recent-activity bubble ("See all"), right where
              the history actually accumulates. */}
        </div>

        {/* ── Storefront (2026-08). Sell Playlists is LIVE (Setlist sales,
            v4 gallery) so it leads; Sell Merch stays a coming-soon teaser
            beneath it (founder 8/29: live product above the teaser).
            IN THE APP since 1.5.3 (founder 11/02): talent list and manage
            playlists here. Fans still can't buy inside the app, that gate is
            digitalSalesAllowed in hooks/useBuyCheckout (App Review 3.1.1, our
            reply of 2026-09-15). Sell Merch stays off the phone: a "Coming
            soon" teaser is exactly the placeholder guideline 2.1 rejects, and
            the reviewer uses a DJ demo login and lands on this page.
            STILL PARKED ON THE WEB (founder 9/19, build 38: "awesome, but not
            for v1") — except for talent who ALREADY use it: a price set, sales
            switched on, or a playlist on their shelf. They keep every control,
            so nobody is left selling something they can no longer manage.
            Un-park the web = drop the `usesStorefront` condition, the day
            Apple approves 1.5.3. ── */}
        {(nativeApp || usesStorefront) && (
          <>
            <div className="profile-sec-label">{tr("profilePage.storefront")}</div>
            <div className="profile-menu">
              <button
                type="button"
                className="profile-menu-row profile-menu-row--sell"
                onClick={() => (storefrontLocked ? openMoneyGate(profile) : setOpenSheet("setlists"))}
              >
                <span className="profile-menu-ic" aria-hidden="true">
                  {PROFILE_ICONS.playlists}
                </span>
                <span className="profile-menu-label">{tr("profilePage.sellPlaylists")}</span>
                <span className={`profile-menu-val profile-sell-state${sellingPlaylists ? " profile-sell-state--on" : ""}`}>
                  {sellingPlaylists ? tr("profilePage.sellingOn") : tr("profilePage.sellingOff")}
                </span>
                <FaChevronRight className="profile-menu-chev" aria-hidden />
              </button>
              {!nativeApp && (
                <button type="button" className="profile-menu-row" onClick={() => setOpenSheet("merch")}>
                  <span className="profile-menu-ic profile-menu-ic--emoji" aria-hidden="true">🧢</span>
                  <span className="profile-menu-label">{tr("profilePage.sellMerch")}</span>
                  <span className="profile-soon-pill">{tr("profilePage.comingSoon")}</span>
                  <FaChevronRight className="profile-menu-chev" aria-hidden />
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Tier Level drawer — the loyalty ladder (V2 medal + road).
            Mounted only when the server said enabled; ships dark. ── */}
        {feeTier?.enabled && feeTier.tier && (
          <FeeTierSheet
            open={openSheet === "feeTier"}
            onClose={() => setOpenSheet(null)}
            status={feeTier}
          />
        )}

        {showRefer && !referLocked && referrals && (
          <ReferEarnSheet
            open={openSheet === "refer"}
            onClose={() => setOpenSheet(null)}
            stats={referrals}
            onCodeChanged={(code) => setReferrals({ ...referrals, code })}
            // "Add to my balance" (1.5.2): the fresh numbers after an add; no
            // bank → the same money gate as every money button; the success
            // sheet's "See Balance" → the Balance tab.
            onCashoutChanged={(cashout) => {
              const next = { ...referrals, cashout };
              if (!cashout) delete next.cashout;
              if (user?.slug) pageCache.set(`referrals:${user.slug}`, next);
              setReferrals(next);
            }}
            onConnectBank={() => {
              setOpenSheet(null);
              openConnectBank(profile);
            }}
            onSeeBalance={() => {
              setOpenSheet(null);
              navigate("/balance");
            }}
            // "Refer 5 talent → jump a tier" (1.5.1 #11) rides the fee-tier
            // payload this page already fetched — no extra call.
            tierJump={
              feeTier?.enabled && feeTier.referralBoost
                ? { ...feeTier.referralBoost, nextName: feeTier.next?.name ?? null, tierName: feeTier.tier?.name ?? null }
                : null
            }
          />
        )}

        {/* Leaderboard — slides up like Stats and Refer & Earn (founder,
            1.5.2 b48: the jump to another page felt different). */}
        <BottomSheet open={openSheet === "leaderboard"} title={tr("profilePage.leaderboard")} icon={TROPHY_ICON} onClose={() => setOpenSheet(null)} tall className="lb-bsheet">
          <LeaderboardViews />
        </BottomSheet>

        <DiscoverSheet open={openSheet === "discover"} onClose={() => setOpenSheet(null)} onOpen={openFromDiscover} />

        <EditProfileSheet
          open={openSheet === "edit"}
          onClose={() => setOpenSheet(null)}
          // Tier Level lives in this drawer's second card now (build 38) — the
          // Profile list row is gone. Same sheet, mounted at page level below.
          feeTier={feeTier?.enabled && feeTier.tier ? feeTier : null}
          onOpenTier={() => setOpenSheet("feeTier")}
          onPreview={() => {
            setOpenSheet(null);
            openPreview();
          }}
          previewHint={previewHint}
          // No bank yet: the tier card stays hidden and opens Connect Bank.
          moneyLocked={moneyLocked}
          onConnectBank={() => {
            setOpenSheet(null);
            openMoneyGate(profile);
          }}
          profile={profile}
          igStatus={igVerify}
          onConnectInstagram={profile.instagramLoginEnabled && profile.instagramDmVerifyEnabled ? () => openIgConnect("connect") : undefined}
          avatar={avatar}
          autosave={autosave}
          name={name}
          setName={setName}
          bio={bio}
          setBio={setBio}
          venueName={venueName}
          setVenueName={setVenueName}
          currency={currency}
          handleCurrencyChange={handleCurrencyChange}
          instagram={instagram}
          setInstagram={setInstagram}
          links={links}
          handleChangeLink={handleChangeLink}
          handleRemoveLink={handleRemoveLink}
          saveLinks={saveLinks}
        />

        {igConnect && (
          <ProfileIgConnect
            key={igConnect.n}
            entry={igConnect.entry}
            profile={profile}
            name={name.trim() || profile.name}
            onProfile={(data) => {
              pageCache.set(`profile:${data.slug || profile.slug}`, data);
              setProfile(data);
              setInstagram((data.instagram || "").replace(/^@+/, ""));
            }}
            onTaken={() => {
              // Today's line in the drawer: "already on another Request App account".
              setIgConnect(null);
              setIgVerify("taken");
            }}
            onClose={() => setIgConnect(null)}
          />
        )}

        <ProfileSettingsSheet
          open={openSheet === "settings"}
          onClose={() => setOpenSheet(null)}
          profile={profile}
          user={user}
          settings={settings}
          autosave={autosave}
          onNeedHelp={() => {
            setOpenSheet(null);
            setHelpSignal((n) => n + 1);
          }}
        />

        <StorefrontSheets
          openSheet={openSheet}
          onClose={() => setOpenSheet(null)}
          profile={profile}
          currency={currency}
          avatarPreview={avatarPreview}
          avatarSrc={avatarSrc}
          fieldStatus={fieldStatus}
          storefront={storefront}
        />

        {/* --- Pending Payout Widget (deferred-Stripe v1) — Ghost-DJ surface,
            shown only when part of the balance is platform-held (see
            platformPendingDollars above). --- */}
        {pendingBalance && platformPendingDollars > 0 && (
          <PendingPayoutWidget
            totalPendingDollars={totalPendingDollars}
            totalPendingCount={totalPendingCount}
            onTransfer={() => navigate("/requests")}
          />
        )}

        {/* ── Merged "More" menu (2026-07 tab consolidation). The former More
            tab now lives here, beneath Preferences: Leaderboard, Feedback,
            Contact, Terms/Privacy + account actions. (Request History moved to
            the Requests page's recent-activity bubble 2026-08; Support was
            retired.) Embedded so it drops the standalone page heading/chrome.
            Edit-mode only. ── */}
        <MorePage
          embedded
          openHelpSignal={helpSignal}
          onHelpClosed={() => setOpenSheet("settings")}
          // Log out sits quietly beside "Follow us on Instagram" (founder,
          // 1.5.2: it no longer needs a full row). Signed out means no requests
          // arrive, so it still asks first (as Instagram / Spotify do).
          onLogOut={() => {
            if (!window.confirm(tr("profilePage.logOutOfRequest"))) return;
            logout();
            navigate("/");
          }}
        />

      </div>
      ) : (
        user && (
          <>
            <div className="profile-embed-request">
              <RequestPage
                key={`preview-${user.slug}`}
                profilePreviewSlug={user.slug}
                profilePreviewDraft={{
                  name,
                  bio,
                  instagram,
                  avatarDisplayUrl: previewAvatarUrl,
                  currency,
                  pricePerRequest: previewRequestPriceValue(),
                  venueName,
                  showFastPass,
                  allowPersonalizedMessages,
                }}
              />
            </div>
          </>
        )
      )}

      {cropSrc && <AvatarCropModal cropper={avatar} />}

    </div>
    </>
  );
};

export default ProfilePage;
